import type { ErrorScope } from '@elara/client-core/lib/error-reporting';
import type { WidgetPart } from '@elara/domain/models/schemas/message-schemas';
import type { MessageRef } from '@elara/domain/models/schemas/shortlist-schemas';
import type { WidgetData, WidgetType } from '@elara/domain/models/schemas/widget-schemas';

import type { WidgetConfig } from './widget-registry.js';

import React from 'react';

import { getErrorReporter } from '@elara/client-core/lib/error-reporting';
import { normalizeWidgetName, validateWidgetData } from '@elara/domain/models/schemas/widget-schemas';
import { preprocessWidgetData } from '@elara/domain/models/schemas/widget-spec';

import { getRegisteredWidget, WidgetRegistry } from './widget-registry.js';
import { WidgetErrorState } from './WidgetErrorState.js';

/** Context passed to widget rendering for error tracking */
export interface WidgetRenderContext {
  /** Reference to the message containing this widget */
  messageRef?: MessageRef;
  /** Tool call reference for data resolution tracking */
  dataRef?: string;
}

// ============================================================================
// Error Capture Helpers
// ============================================================================

/** Sets common error tags from widget render context */
function setWidgetErrorTags({ scope, index, ctx }: { scope: ErrorScope; index: number; ctx?: WidgetRenderContext }): void {
  scope.setTag('partIndex', String(index));
  if (ctx?.messageRef?.chatId) scope.setTag('chatId', ctx.messageRef.chatId);
  if (ctx?.messageRef?.messageId) scope.setTag('messageId', ctx.messageRef.messageId);
}

/** Captures a widget validation error with full context */
function captureWidgetValidationError({
  error,
  widgetType,
  index,
  data,
  ctx,
}: {
  error: unknown;
  widgetType: WidgetType;
  index: number;
  data: unknown;
  ctx?: WidgetRenderContext;
}): void {
  if (process.env.NODE_ENV !== 'test') {
    console.error(
      `[handled] Error rendering widget of type ${widgetType}:`,
      error instanceof Error ? error.message : String(error),
      { widgetType, index },
    );
  }
  const reporter = getErrorReporter();
  reporter.withScope((scope) => {
    scope.setTag('widgetType', widgetType);
    setWidgetErrorTags({ scope, index, ctx });
    scope.setContext('widget', {
      type: widgetType,
      index,
      dataRef: ctx?.dataRef,
      dataType: Array.isArray(data) ? 'array' : typeof data,
      dataKeys: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data).join(', ') : undefined,
      dataPreview: JSON.stringify(data)?.slice(0, 500), // Truncated to avoid PII/large payloads
    });
    if (ctx?.messageRef) {
      scope.setContext('message', { chatId: ctx.messageRef.chatId, messageId: ctx.messageRef.messageId });
    }
    reporter.captureException(error);
  });
}

/** Captures an unknown widget type error with context */
function captureUnknownWidgetError({
  type,
  index,
  ctx,
}: {
  type: string | undefined;
  index: number;
  ctx: WidgetRenderContext;
}): void {
  const reporter = getErrorReporter();
  reporter.withScope((scope) => {
    scope.setLevel('error');
    setWidgetErrorTags({ scope, index, ctx });
    scope.setContext('widget', { type: type || 'undefined', index, dataRef: ctx.dataRef });
    reporter.captureMessage(`Unknown widget type: ${type || 'undefined'}`);
  });
}

// ============================================================================
// Widget Rendering
// ============================================================================

/**
 * Type-safe widget renderer with validation and error tracking.
 */
export function renderWidget<WT extends WidgetType>({
  widgetType,
  data,
  index,
  ctx,
}: {
  widgetType: WT;
  data: WidgetData<WT>;
  index: number;
  ctx?: WidgetRenderContext;
}): React.ReactNode {
  try {
    const normalizedData = preprocessWidgetData(data);
    const validationResult = validateWidgetData(widgetType, normalizedData);

    if (!validationResult.success) {
      captureWidgetValidationError({ error: validationResult.error, widgetType, index, data: normalizedData, ctx });
      return (
        <WidgetErrorState
          key={`error-${index}`}
          message={`Invalid ${widgetType} data`}
          error={validationResult.error}
        />
      );
    }

    const widgetConfig = getRegisteredWidget(widgetType) as WidgetConfig<any, any, React.ReactNode>;
    const props = widgetConfig.makeReactProps(validationResult.data);
    return <widgetConfig.component key={`widget-${index}`} {...props} />;
  } catch (error) {
    captureWidgetValidationError({ error, widgetType, index, data, ctx });
    return <WidgetErrorState key={`error-${index}`} message={`Error rendering ${widgetType} widget`} error={error} />;
  }
}

/**
 * Renders a widget from a WidgetPart.
 *
 * Widget data is denormalized on the server side, so `data` should always be present.
 * Data resolution priority:
 * 1. Direct `data` property (denormalized, preferred)
 * 2. Fallback to `dataRef` lookup (backward compatibility)
 */
export function renderWidgetFromPart({
  part,
  index,
  ctx,
}: {
  part: WidgetPart;
  index: number;
  ctx?: WidgetRenderContext;
}): React.ReactNode | undefined {
  const { type, payload } = part.widget;

  // Normalize legacy widget names for backwards compatibility
  const normalizedType = normalizeWidgetName(type);

  // Enrich context with widget-specific info from the part
  const enrichedCtx: WidgetRenderContext = { ...ctx, dataRef: payload?.['dataRef'] ?? ctx?.dataRef };

  if (!normalizedType || !WidgetRegistry[normalizedType]) {
    console.error(`Unknown widget type: ${type || 'undefined'}`, { part });
    captureUnknownWidgetError({ type, index, ctx: enrichedCtx });
    return;
  }

  const widgetData = payload.data as WidgetData<typeof normalizedType>;
  return renderWidget({ widgetType: normalizedType, data: widgetData, index, ctx: enrichedCtx });
}
